import { PageHeader } from "../../../../components/Headers/PageHeader";
import { Button } from "../../../../components/New/Button";
import { Loading } from "../../../../components/New/Loading";
import { SearchInput } from "../../../../components/New/SearchInput";
import { NoDataAvailableImage } from "../../../../components/NoDataAvailableImage";
import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "../../../../components/New/Table";
import { Modal } from "../../../../components/New/Modal";
import { AutoPrintModal } from "./components/AutoPrintModal";
import {
  useAutoPrint,
  AutoPrintData,
} from "../../../../hooks/api/Inventory/useAutoPrint";
import { useTranslation } from "react-i18next";

import * as S from "./styles";
import { useMediaQuery } from "usehooks-ts";
import { useTheme } from "styled-components";
import { DrawerComponent } from "../../../../components/New/Drawer";
import { formatDateWithTenantFormat } from "../../../../shared/Formatter";
import { useAuth } from "../../../../hooks/auth";

export function AutoPrint() {
  const { t, i18n } = useTranslation();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const theme = useTheme();
  const isResponsive = useMediaQuery(
    `(max-width: ${theme.responsiveDesign.sm})`,
  );

  const isEnglishLanguage = i18n.language.startsWith("en");
  const { shopSelected } = useAuth();

  const {
    autoPrintData,
    loading,
    pagination,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    fetchAutoPrint,
  } = useAutoPrint();

  const getAutoPrintStatusTranslation = (
    status: AutoPrintData["status"],
  ): string => {
    switch (status) {
      case "IN_PROGRESS":
        return t("autoPrintStatusInProgress");
      case "STANDING_BY":
        return t("autoPrintStatusStandingBy");
      case "COMPLETED":
        return t("autoPrintStatusCompleted");
      case "ERROR":
        return t("autoPrintStatusError");
      case "CANCELED":
        return t("autoPrintStatusCanceled");
      default:
        return "";
    }
  };

  const dateFormat = shopSelected?.tenant?.dateFormat;

  const autoPrintColumns: ColumnDef<AutoPrintData>[] = [
    {
      accessorKey: "id",
      header: t("id"),
      size: 191,
      cell: (props) => <p>{props.getValue()}</p>,
    },
    {
      accessorKey: "name",
      header: t("name"),
      size: 200,
      cell: (props) => <p>{props.getValue()}</p>,
    },
    {
      accessorKey: "createdAt",
      header: t("date"),
      size: 180,
      cell: (props) => {
        const dateString = props.getValue() as string;
        return (
          <p>{formatDateWithTenantFormat(dateString, dateFormat, true)}</p>
        );
      },
    },
    {
      accessorKey: "status",
      header: t("status"),
      size: 265,
      cell: ({ row }) => (
        <S.RolesContainer status={row.original.status}>
          <p>{getAutoPrintStatusTranslation(row.original.status)}</p>
        </S.RolesContainer>
      ),
    },
  ];

  const handleSearchSubmit = () => {
    handleSearch(searchValue);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  return (
    <S.Container>
      <PageHeader
        title={t("priceLabelsAutoPrint")}
        description={t("autoPrintLabels")}
        customButton
        customBreadcrumbString={t("priceLabelsAutoPrint")}
      >
        {!isResponsive && (
          <Modal
            customButtons
            modalContent={<AutoPrintModal onSuccess={fetchAutoPrint} />}
            title={t("newAutoPrint")}
            width="33.75rem"
          >
            <Button
              title={t("newAutoPrint")}
              width={isEnglishLanguage ? "8.25rem" : "100%"}
            />
          </Modal>
        )}

        {isResponsive && (
          <Button
            title={t("newAutoPrint")}
            width="100%"
            height="2.5rem"
            onClick={(e) => {
              e.preventDefault();
              setFilterDrawerOpen(true);
            }}
          />
        )}
      </PageHeader>

      <S.Content>
        <SearchInput
          onPressEnter={handleSearchSubmit}
          onChange={handleSearchChange}
          value={searchValue}
        />

        {loading && <Loading />}

        {autoPrintData.length === 0 && !loading && <NoDataAvailableImage />}

        {autoPrintData.length > 0 && !loading && (
          <Table
            columns={autoPrintColumns}
            columnToFilter="name"
            data={autoPrintData}
            key={autoPrintData.length}
            pagination={{
              rowsPerPage: pagination.rowsPerPage,
              selectPage: handlePageChange,
              selectRowsPerPage: handleRowsPerPageChange,
              page: pagination.currentPage,
              totalPages: pagination.totalPages,
            }}
          />
        )}

        {isResponsive && (
          <DrawerComponent
            open={filterDrawerOpen}
            onOpenChange={setFilterDrawerOpen}
            variant="filter"
            description=""
            triggerContent={null}
            width="100%"
            title={t("newAutoPrint")}
            showCloseButton
          >
            <AutoPrintModal
              isResponsive={isResponsive}
              setCloseDrawer={() => setFilterDrawerOpen(false)}
              onSuccess={fetchAutoPrint}
            />
          </DrawerComponent>
        )}
      </S.Content>
    </S.Container>
  );
}
